import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../../../../AppContext";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { IHandoverDayModel } from "../models/handoverNoteModel";

/** Longest message the API accepts; mirrors HandoverContentLimits.MaxContentLength. */
export const HANDOVER_MAX_CONTENT_LENGTH = 2000;

/** Turns a Date into the studio-local yyyy-MM-dd key the API groups a day by. */
export const toHandoverDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

interface IUseHandoverNoteParams {
  /** The day currently selected in the planner calendar. */
  selectedDate: Date;
}

/**
 * Owns the hand-over thread for the planner's selected day: the pin's preview and
 * unread dot, the discussion dialog, and marking the day read the moment it opens.
 */
export const useHandoverNote = ({ selectedDate }: IUseHandoverNoteParams) => {
  const { selectedStudioId } = useAppContext();
  const queryClient = useQueryClient();
  const { mutate } = useApiMutation();

  const dateKey = useMemo(
    () => toHandoverDateKey(selectedDate),
    [selectedDate],
  );

  // The day the thread was opened for, not a bare boolean: read marking must never
  // depend on which effect happens to be declared first when the planner moves to
  // another day while the dialog is up.
  const [openDateKey, setOpenDateKey] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const queryKey = useMemo(
    () => [endpointsMetadata.planner.getHandoverDay, selectedStudioId, dateKey],
    [selectedStudioId, dateKey],
  );

  const { data: day, isLoading } = useApiQuery<IHandoverDayModel>({
    queryKey,
    endpoint: `${endpointsMetadata.planner.getHandoverDay}?date=${dateKey}`,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
    disableErrorToast: true,
    retry: false,
  });

  const entries = day?.entries ?? [];
  const entriesCount = day?.entriesCount ?? 0;
  const hasEntries = entriesCount > 0;
  const unreadCount = day?.unreadCount ?? 0;
  const hasUnread = unreadCount > 0;
  const previewContent = day?.latestEntryContent ?? null;
  const previewAuthor = day?.latestEntryCreatedBy ?? null;

  const refreshDay = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const isDialogOpen = openDateKey === dateKey;

  const openDialog = useCallback(() => setOpenDateKey(dateKey), [dateKey]);

  const closeDialog = useCallback(() => {
    setOpenDateKey(null);
    setDraft("");
  }, []);

  // Simply opening the thread counts as reading it - no extra click, exactly as a
  // note pinned to your shift should behave. The refresh afterwards clears the dot.
  useEffect(() => {
    if (!isDialogOpen || !hasUnread || selectedStudioId <= 0) {
      return;
    }

    mutate(
      {
        endpoint: endpointsMetadata.planner.markHandoverDayAsRead,
        method: "post",
        payload: { date: dateKey },
        disableSuccessToast: true,
        disableErrorToast: true,
      },
      { onSuccess: refreshDay },
    );
  }, [isDialogOpen, hasUnread, dateKey, selectedStudioId, mutate, refreshDay]);

  // Switching days while the thread is up would silently show another day's messages.
  // The dialog is already closed by the key comparison above; this only clears the
  // half-typed message, which belonged to the day the user has just left.
  useEffect(() => {
    setOpenDateKey(null);
    setDraft("");
  }, [dateKey, selectedStudioId]);

  const trimmedDraft = draft.trim();
  const canSubmit =
    trimmedDraft.length > 0 &&
    trimmedDraft.length <= HANDOVER_MAX_CONTENT_LENGTH &&
    !isSaving;

  const addEntry = useCallback(() => {
    if (!canSubmit) {
      return;
    }

    setIsSaving(true);
    mutate(
      {
        endpoint: endpointsMetadata.planner.addHandoverEntry,
        method: "post",
        payload: { date: dateKey, content: trimmedDraft },
        disableSuccessToast: true,
      },
      {
        onSuccess: () => {
          setDraft("");
          refreshDay();
        },
        onSettled: () => setIsSaving(false),
      },
    );
  }, [canSubmit, dateKey, mutate, refreshDay, trimmedDraft]);

  // Enter sends, Shift+Enter breaks the line. A hand-over note is a chat message,
  // and reaching for the button after every one-liner gets old fast.
  const handleComposerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== "Enter" || event.shiftKey) {
        return;
      }

      // An IME candidate list is also confirmed with Enter; submitting there would
      // eat the word the user is still composing.
      if (event.nativeEvent.isComposing) {
        return;
      }

      event.preventDefault();
      addEntry();
    },
    [addEntry],
  );

  const deleteEntry = useCallback(
    (entryId: number) => {
      showCustomConfirmDialog(
        "Сигурни ли сте, че искате да изтриете тази бележка?",
        () =>
          mutate(
            {
              endpoint: `${endpointsMetadata.planner.deleteHandoverEntry}?entryId=${entryId}`,
              method: "delete",
              disableSuccessToast: true,
            },
            { onSuccess: refreshDay },
          ),
        () => {},
        "warning",
        "Да",
        "Не",
      );
    },
    [mutate, refreshDay],
  );

  return {
    dateKey,
    isLoading,
    entries,
    entriesCount,
    hasEntries,
    unreadCount,
    hasUnread,
    previewContent,
    previewAuthor,
    isDialogOpen,
    openDialog,
    closeDialog,
    draft,
    setDraft,
    hasDraft: trimmedDraft.length > 0,
    canSubmit,
    isSaving,
    addEntry,
    handleComposerKeyDown,
    deleteEntry,
  };
};
